import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, Column, CellProps } from 'react-table'
import { useParams } from 'react-router-dom'

import type { ChipProps } from '@mui/material'
import {
    Box,
    Chip,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
    Checkbox,
    Button,
    Tooltip,
    IconButton,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import HelpIcon from '@mui/icons-material/Help'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

import { Consultation, ConsultationStatus, PaymentType, DesmosActStatus, BillingReview, BillingReviewStatus, BillingIssueReason } from '@/core/models/consultation.model'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import CalendarIcon from '@/components/ui/icons/Calendar'
import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'
import PdsIcon from '@/components/ui/icons/Pds'
import NoResults from '@/assets/svg/no-results.svg?react'
import Pagination from '@/components/ui/pagination/Pagination'
import DesmosActsDetailDialog from './DesmosActsDetailDialog'
import MassBillingDialog from './MassBillingDialog'
import ReceiptIcon from '@mui/icons-material/Receipt'
import LaunchIcon from '@mui/icons-material/Launch'
import { formatPrice } from '@/utils/formatPrice'

type Props = {
    consultations: Consultation[]
    isLoading: boolean
    page: number
    onPageChange?: (newPage: number) => void
    onRowsPerPageChange?: (rowsPerPage: number) => void
    rowsPerPageOptions?: number[]
    totalCount: number
    onValidateSelection?: (selectedIds: string[]) => void
    showPaymentInfo?: boolean
    showDesmosStatus?: boolean
    selectedRows?: string[]
    onSelectedRowsChange?: (ids: string[]) => void
}

const defaultRowsPerPageOptions = [5, 10, 15]

type ConsultationTableData = {
    id: string
    startDate: string
    endDate: string
    waitingRoomJoinedAt?: string
    waitingTime: number
    consultationTime: number
    status: string
    price?: number
    paymentType?: PaymentType
    doctor?: {
        gender?: 'M' | 'F' | 'I' | 'O'
        name: string
        surname: string
        establishmentId?: string
        establishmentName?: string
    }
    nurse?: {
        gender?: 'M' | 'F' | 'I' | 'O'
        name: string
        surname: string
    }
    patient: {
        gender?: 'M' | 'F' | 'I' | 'O'
        name: string
        surname: string
        secu?: string
        regime?: string
        caisse?: string
        gestion?: string
    }
    desmosActStatus?: DesmosActStatus
    billingReview?: BillingReview
}

const DesmosStatusIcon = ({ status, onClick, t }: { status?: DesmosActStatus, onClick?: () => void, t: any }) => {
    const baseIconProps = {
        fontSize: "small" as const,
        sx: onClick ? { cursor: 'pointer' } : {}
    };

    if (!status) {
        return (
            <Tooltip title={t('page.network.desmos.statusTooltip.noAttempt')}>
                <IconButton onClick={onClick} size="small">
                    <HelpIcon color="disabled" {...baseIconProps} />
                </IconButton>
            </Tooltip>
        )
    }

    if (status.success) {
        if (status.acts && status.acts.length > 0) {
            // Check if all acts were successful
            const allSuccessful = status.acts.every(act => act.success);
            if (allSuccessful) {
                return (
                    <Tooltip title={t('page.network.desmos.statusTooltip.allSuccess', { count: status.acts.length })}>
                        <IconButton onClick={onClick} size="small">
                            <CheckCircleIcon color="success" {...baseIconProps} />
                        </IconButton>
                    </Tooltip>
                )
            } else {
                // Some acts failed
                const successfulCount = status.acts.filter(act => act.success).length;
                return (
                    <Tooltip title={t('page.network.desmos.statusTooltip.someFailed', { successCount: successfulCount, total: status.acts.length })}>
                        <IconButton onClick={onClick} size="small">
                            <WarningIcon color="warning" {...baseIconProps} />
                        </IconButton>
                    </Tooltip>
                )
            }
        } else {
            return (
                <Tooltip title={t('page.network.desmos.statusTooltip.withMessage', { message: status.message || t('page.network.desmos.details.noActsConfigured') })}>
                    <IconButton onClick={onClick} size="small">
                        <HelpIcon color="disabled" {...baseIconProps} />
                    </IconButton>
                </Tooltip>
            )
        }
    } else {
        return (
            <Tooltip title={t('page.network.desmos.statusTooltip.withRetry', { message: status.message || t('page.network.desmos.details.error') })}>
                <IconButton onClick={onClick} size="small">
                    <ErrorIcon color="error" {...baseIconProps} />
                </IconButton>
            </Tooltip>
        )
    }
}

function useIssueReasonLabel(): Record<BillingIssueReason, string> {
    const { t } = useTranslation()
    return {
        [BillingIssueReason.DesmosDown]: t('page.consultation.billingReview.reason.desmosDown', { defaultValue: 'Desmos indisponible' }),
        [BillingIssueReason.NoSocialSecurity]: t('page.consultation.billingReview.reason.noSocialSecurity', { defaultValue: 'Pas de numéro de sécurité sociale' }),
        [BillingIssueReason.PatientRefused]: t('page.consultation.billingReview.reason.patientRefused', { defaultValue: 'Patient a refusé' }),
        [BillingIssueReason.TechnicalIssue]: t('page.consultation.billingReview.reason.technicalIssue', { defaultValue: 'Problème technique' }),
        [BillingIssueReason.MissingInfo]: t('page.consultation.billingReview.reason.missingInfo', { defaultValue: 'Informations manquantes' }),
        [BillingIssueReason.Other]: t('page.consultation.billingReview.reason.other', { defaultValue: 'Autre' }),
    }
}

function BillingReviewChip({ review, size = 'small' as const }: { review?: BillingReview; size?: 'small' | 'medium' }) {
    const { t } = useTranslation()
    const issueReasonLabel = useIssueReasonLabel()
    if (!review || review.status === BillingReviewStatus.Pending) {
        return <Chip size={size} icon={<HourglassEmptyIcon />} label={t('page.consultation.billingReview.status.pending', { defaultValue: 'En attente' })} variant="outlined" />
    }
    if (review.status === BillingReviewStatus.Completed) {
        return <Chip size={size} icon={<CheckCircleIcon />} label={t('page.consultation.billingReview.status.completed', { defaultValue: 'Terminé' })} color="success" />
    }
    const reason = review.issueReason ? issueReasonLabel[review.issueReason] : ''
    const tooltipText = [reason, review.note].filter(Boolean).join(' — ')
    return (
        <Tooltip title={tooltipText || ''}>
            <Chip size={size} icon={<WarningIcon />} label={reason || t('page.consultation.billingReview.status.issue', { defaultValue: 'Problème' })} color="error" />
        </Tooltip>
    )
}

export default function WaitingRoomConsultationsTable({
    consultations,
    isLoading,
    page,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    totalCount,
    onValidateSelection,
    showPaymentInfo = false,
    showDesmosStatus = false,
    selectedRows: controlledSelectedRows,
    onSelectedRowsChange,
}: Props) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { networkId, waitingRoomId } = useParams<{ networkId: string; waitingRoomId: string }>()
    const [internalSelectedRows, setInternalSelectedRows] = useState<string[]>([])

    // Support both controlled (from parent) and uncontrolled selection
    const selectedRows = controlledSelectedRows ?? internalSelectedRows
    const setSelectedRows = onSelectedRowsChange ?? setInternalSelectedRows

    const [showBillingDetails, setShowBillingDetails] = useState(false)
    const [desmosDialogOpen, setDesmosDialogOpen] = useState(false)
    const [selectedConsultation, setSelectedConsultation] = useState<ConsultationTableData | null>(null)
    const [massBillingDialogOpen, setMassBillingDialogOpen] = useState(false)

    const data = useMemo<ConsultationTableData[]>(
        () =>
            consultations.map((consultation) => {
                const joinedAt = consultation.waitingRoomJoinedAt
                const waitingTime = joinedAt ? dayjs(joinedAt).diff(dayjs(consultation.startDate), 'minute') : 0
                const consultationTime = consultation.endDate
                    ? dayjs(consultation.endDate).diff(dayjs(joinedAt || consultation.startDate), 'minute')
                    : 0
                return {
                id: consultation._id,
                startDate: consultation.startDate,
                endDate: consultation.endDate || '',
                waitingRoomJoinedAt: consultation.waitingRoomJoinedAt,
                waitingTime,
                consultationTime,
                status: consultation.status,
                price: consultation.price,
                paymentType: consultation.paymentType,
                doctor: consultation.medecin
                    ? {
                        gender: consultation.medecin.userInfo?.gender,
                        name: consultation.medecin.userInfo?.name,
                        surname: consultation.medecin.userInfo?.surname,
                        establishmentId: consultation.medecin.establishment?._id,
                        establishmentName: consultation.medecin.establishment?.name,
                    }
                    : undefined,
                nurse: consultation.pds
                    ? {
                        gender: consultation.pds.userInfo?.gender,
                        name: consultation.pds.userInfo?.name,
                        surname: consultation.pds.userInfo?.surname,
                    }
                    : undefined,
                patient: {
                    gender: consultation.patient.userInfo.gender,
                    name: consultation.patient.userInfo.name,
                    surname: consultation.patient.userInfo.surname,
                    secu: consultation.patient.secu,
                    regime: consultation.patient.codeRegime,
                    caisse: consultation.patient.caisse,
                    gestion: consultation.patient.codeGestion,
                },
                desmosActStatus: consultation.desmosActStatus,
                billingReview: consultation.billingReview,
            }}),
        [consultations]
    )

    const handleChangePage = (newPage: number) => {
        if (page === newPage) return
        onPageChange?.(newPage)
    }

    const handleSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedRows(data.map(row => row.id))
        } else {
            setSelectedRows([])
        }
    }, [data])

    const handleSelectRow = useCallback((id: string) => {
        if (selectedRows.includes(id)) {
            setSelectedRows(selectedRows.filter(rowId => rowId !== id))
        } else {
            setSelectedRows([...selectedRows, id])
        }
    }, [selectedRows, setSelectedRows])

    const handleDesmosStatusClick = useCallback((consultation: ConsultationTableData) => {
        setSelectedConsultation(consultation)
        setDesmosDialogOpen(true)
    }, [])

    const handleCloseDesmosDialog = useCallback(() => {
        setDesmosDialogOpen(false)
        setSelectedConsultation(null)
    }, [])

    const handleMassBilling = useCallback(() => {
        if (selectedRows.length > 0) {
            setMassBillingDialogOpen(true)
        }
    }, [selectedRows.length])

    const unbilledOrFailedIds = useMemo(() => {
        if (!showDesmosStatus) return []
        return data.filter(row => {
            if (!row.desmosActStatus) return true // Never attempted
            if (!row.desmosActStatus.success) return true // Overall failure
            if (row.desmosActStatus.acts?.some(act => !act.success)) return true // Some acts failed
            return false
        }).map(row => row.id)
    }, [data, showDesmosStatus])

    const handleBillUnbilled = useCallback(() => {
        if (unbilledOrFailedIds.length > 0) {
            // Select the unbilled rows visually first so user sees what will be billed
            setSelectedRows(unbilledOrFailedIds)
        }
    }, [unbilledOrFailedIds])

    const handleCloseMassBillingDialog = useCallback(() => {
        setMassBillingDialogOpen(false)
        setSelectedRows([]) // Clear selection after mass billing
    }, [])

    const getPaymentTypeLabel = (type?: PaymentType) => {
        if (!type) return '-'
        const labels = {
            [PaymentType.Card]: t('common:payment.type.card'),
            [PaymentType.Cash]: t('common:payment.type.cash'),
            [PaymentType.Check]: t('common:payment.type.check'),
            [PaymentType.Transfer]: t('common:payment.type.transfer')
        }
        return labels[type] || type
    }

    // Memoize selection states to prevent unnecessary re-renders
    const selectAllChecked = useMemo(() => data.length > 0 && selectedRows.length === data.length, [data.length, selectedRows.length])
    const selectAllIndeterminate = useMemo(() => selectedRows.length > 0 && selectedRows.length < data.length, [data.length, selectedRows.length])

    const columns = useMemo<Column<ConsultationTableData>[]>(
        () => [
            {
                id: 'selection',
                Header: ({ getToggleAllRowsSelectedProps }: any) => (
                    <Checkbox
                        indeterminate={selectAllIndeterminate}
                        checked={selectAllChecked}
                        onChange={handleSelectAll}
                    />
                ),
                Cell: ({ row }: { row: any }) => (
                    <Checkbox
                        checked={selectedRows.includes(row.original.id)}
                        onChange={() => handleSelectRow(row.original.id)}
                    />
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'actions',
                Header: '',
                Cell: ({ row }: { row: any }) => (
                    <Tooltip title={t('common.button.open', 'Open')}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                window.open(`/consultations/${row.original.id}`, '_blank')
                            }}
                        >
                            <LaunchIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'startDate',
                Header: t('page.consultations.table.header.startDate'),
                accessor: 'startDate',
                Cell: ({ value }: CellProps<ConsultationTableData, string>) => (
                    <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
                        <CalendarIcon fontSize="small" />
                        <Chip
                            color="secondary"
                            label={
                                <span>
                                    {dayjs(value).isSame(dayjs(), 'day') ? t('moment:calendar:today') : dayjs(value).format('DD/MM/YYYY')} -{' '}
                                    <b>{dayjs(value).format('HH[h]mm')}</b>
                                </span>
                            }
                            variant="filled"
                        />
                    </Stack>
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'waitingTime',
                Header: t('page.consultations.table.header.waitingTime', { defaultValue: 'Wait' }),
                accessor: 'waitingTime',
                Cell: ({ value }: CellProps<ConsultationTableData, number>) => (
                    <Chip
                        color={value > 30 ? 'warning' : 'default'}
                        size="small"
                        label={value ? `${value} min` : '-'}
                        variant="filled"
                    />
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'consultationTime',
                Header: t('page.consultations.table.header.consultationTime', { defaultValue: 'Duration' }),
                accessor: 'consultationTime',
                Cell: ({ value }: CellProps<ConsultationTableData, number>) => (
                    <Chip
                        color="default"
                        size="small"
                        label={value ? `${value} min` : '-'}
                        variant="filled"
                    />
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'status',
                Header: t('page.consultations.table.header.status'),
                accessor: 'status',
                Cell: ({ value }: CellProps<ConsultationTableData, string>) => {
                    let color: ChipProps['color']
                    switch (value) {
                        case 'scheduled':
                        case 'waiting':
                            color = 'primary'
                            break
                        case 'accepted':
                            color = 'success'
                            break
                        case 'canceled':
                        case 'refused':
                            color = 'error'
                            break
                        default:
                            color = 'info'
                    }
                    return <Chip
                        color={color}
                        label={t(`common:model.consultation.status.${value}.label`)}
                        size="small"
                        variant="filled"
                    />
                },
            } as Column<ConsultationTableData>,
            {
                id: 'billingReview',
                Header: t('page.consultations.table.header.billingReview', { defaultValue: 'Billing' }),
                accessor: 'billingReview',
                Cell: ({ value }: CellProps<ConsultationTableData, BillingReview | undefined>) => (
                    <BillingReviewChip review={value} />
                ),
            } as Column<ConsultationTableData>,
            ...(showDesmosStatus ? [
                {
                    id: 'desmosStatus',
                    Header: t('page.consultations.table.header.desmosStatus', { defaultValue: 'Desmos Status' }),
                    accessor: 'desmosActStatus',
                    Cell: ({ value, row }: CellProps<ConsultationTableData, DesmosActStatus | undefined>) => (
                        <Box display="flex" justifyContent="center">
                            <DesmosStatusIcon
                                status={value}
                                onClick={() => handleDesmosStatusClick(row.original)}
                                t={t}
                            />
                        </Box>
                    ),
                } as Column<ConsultationTableData>,
            ] : []),
            ...(showPaymentInfo ? [
                {
                    id: 'price',
                    Header: t('page.consultations.table.header.price'),
                    accessor: (row) => row.price,
                    Cell: ({ value }: CellProps<ConsultationTableData, number | undefined>) => (
                        <Chip
                            color="default"
                            size="small"
                            label={formatPrice(value)}
                            variant="filled"
                        />
                    ),
                } as Column<ConsultationTableData>,
                {
                    id: 'paymentType',
                    Header: t('page.consultations.table.header.paymentType'),
                    accessor: (row) => row.paymentType,
                    Cell: ({ value }: CellProps<ConsultationTableData, PaymentType | undefined>) => (
                        <Chip
                            color="default"
                            size="small"
                            label={getPaymentTypeLabel(value)}
                            variant="filled"
                        />
                    ),
                } as Column<ConsultationTableData>,
            ] : []),
            {
                id: 'nurse',
                Header: t(`page.consultations.table.header.pds`),
                accessor: 'nurse',
                Cell: ({ value: nurse }: CellProps<ConsultationTableData, ConsultationTableData['nurse']>) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {nurse ? (
                            <>
                                <PdsIcon isFemale={nurse.gender === 'F'} />
                                <span>{_.trim(`${nurse.name} ${nurse.surname}`)}</span>
                            </>
                        ) : (
                            <span>-</span>
                        )}
                    </Stack>
                ),
            } as Column<ConsultationTableData>,
            {
                id: 'doctor',
                Header: t(`page.consultations.table.header.doctor`),
                accessor: 'doctor',
                Cell: ({ value: doctor }: CellProps<ConsultationTableData, ConsultationTableData['doctor']>) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {doctor ? (
                            <>
                                <DoctorIcon isFemale={doctor.gender === 'F'} />
                                <span>{_.trim(`${doctor.name} ${doctor.surname}`)}</span>
                            </>
                        ) : (
                            <span>-</span>
                        )}
                    </Stack>
                ),
            },
            ...(showBillingDetails ? [{
                id: 'doctorEstablishment',
                Header: t('page.consultations.table.header.establishment', { defaultValue: 'Establishment' }),
                accessor: (row: ConsultationTableData) => row.doctor?.establishmentName,
                Cell: ({ value, row }: CellProps<ConsultationTableData, string | undefined>) => (
                    <Tooltip title={row.original.doctor?.establishmentId || ''}>
                        <Chip
                            color="default"
                            size="small"
                            label={value || '-'}
                            variant="outlined"
                        />
                    </Tooltip>
                ),
            } as Column<ConsultationTableData>] : []),
            {
                id: 'patient',
                Header: t('page.consultations.table.header.patient'),
                accessor: 'patient',
                Cell: ({ value: patient }: CellProps<ConsultationTableData, ConsultationTableData['patient']>) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PatientIcon isFemale={patient.gender === 'F'} />
                        <span>{_.trim(`${patient.name} ${patient.surname}`)}</span>
                    </Stack>
                ),
            },
            ...(showBillingDetails ? [
                {
                    id: 'secu',
                    Header: t('page.consultations.table.header.secu'),
                    accessor: (row: ConsultationTableData) => row.patient.secu,
                    Cell: ({ value }: CellProps<ConsultationTableData, string | undefined>) => <span>{value || '-'}</span>,
                },
                {
                    id: 'regime',
                    Header: t('page.consultations.table.header.regime'),
                    accessor: (row: ConsultationTableData) => row.patient.regime,
                    Cell: ({ value }: CellProps<ConsultationTableData, string | undefined>) => <span>{value || '-'}</span>,
                },
                {
                    id: 'caisse',
                    Header: t('page.consultations.table.header.caisse'),
                    accessor: (row: ConsultationTableData) => row.patient.caisse,
                    Cell: ({ value }: CellProps<ConsultationTableData, string | undefined>) => <span>{value || '-'}</span>,
                },
                {
                    id: 'gestion',
                    Header: t('page.consultations.table.header.gestion'),
                    accessor: (row: ConsultationTableData) => row.patient.gestion,
                    Cell: ({ value }: CellProps<ConsultationTableData, string | undefined>) => <span>{value || '-'}</span>,
                },
            ] as Column<ConsultationTableData>[] : []),
        ], [t, selectAllChecked, selectAllIndeterminate, selectedRows, showPaymentInfo, showDesmosStatus, showBillingDetails]
    )

    const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<ConsultationTableData>({
        columns,
        data,
    })

    if (isLoading) {
        return <HeartbeatLoader />
    }

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {!showPaymentInfo && selectedRows.length > 0 && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => onValidateSelection?.(selectedRows)}
                            >
                                {t('page.consultation.validate.button', { defaultValue: 'Marquer comme payé' })} ({selectedRows.length})
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<ReceiptIcon />}
                                onClick={handleMassBilling}
                            >
                                {t('page.network.desmos.massBilling.button')} ({selectedRows.length})
                            </Button>
                        </>
                    )}
                    {showDesmosStatus && unbilledOrFailedIds.length > 0 && (
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<ReceiptIcon />}
                            onClick={handleBillUnbilled}
                        >
                            {t('page.network.desmos.billUnbilled.button', { defaultValue: 'Bill Unbilled/Failed' })} ({unbilledOrFailedIds.length})
                        </Button>
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showBillingDetails}
                                onChange={() => setShowBillingDetails(!showBillingDetails)}
                                color="primary"
                            />
                        }
                        label={t('page.consultation.filter.billingDetails', { defaultValue: 'Billing details' })}
                    />
                </Stack>
                <Pagination
                    color="primary"
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    onItemsPerPageChange={onRowsPerPageChange}
                    totalItems={totalCount}
                />
            </Box>

            {data.length === 0 ? (
                <Stack direction="column" justifyContent="center" alignItems="center">
                    <Box component={NoResults} sx={{ width: 'auto', height: '250px', mt: '10px', mr: '40px' }} />
                    <Typography component="div" variant="body1">
                        {t('No consultations found')}
                    </Typography>
                </Stack>
            ) : !isMobile ? (
                <Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                {headerGroups.map((headerGroup) => (
                                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <TableCell {...column.getHeaderProps()}>{column.render('Header')}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody {...getTableBodyProps()}>
                                {rows.map((row) => {
                                    prepareRow(row)
                                    return (
                                        <TableRow {...row.getRowProps()}>
                                            {row.cells.map((cell) => (
                                                <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                                            ))}
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {/* Mobile select-all */}
                    {!showPaymentInfo && data.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                                size="small"
                                indeterminate={selectAllIndeterminate}
                                checked={selectAllChecked}
                                onChange={handleSelectAll}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {selectedRows.length > 0
                                    ? `${selectedRows.length} / ${data.length} ${t('common.selected', { defaultValue: 'selected' })}`
                                    : t('common.selectAll', { defaultValue: 'Select all' })
                                }
                            </Typography>
                        </Stack>
                    )}
                    {data.map((consultation) => {
                        let statusColor: ChipProps['color'] = 'default'
                        switch (consultation.status) {
                            case 'scheduled': case 'waiting': statusColor = 'primary'; break
                            case 'accepted': statusColor = 'success'; break
                            case 'canceled': case 'refused': statusColor = 'error'; break
                            default: statusColor = 'info'
                        }
                        return (
                            <Paper key={consultation.id} variant="outlined">
                                <Box p={1.5}>
                                    <Stack spacing={1}>
                                        {/* Row 1: checkbox + date + status + open */}
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            {!showPaymentInfo && (
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedRows.includes(consultation.id)}
                                                    onChange={() => handleSelectRow(consultation.id)}
                                                    sx={{ p: 0.5 }}
                                                />
                                            )}
                                            <Stack direction="row" spacing={0.5} alignItems="center" flex={1} flexWrap="wrap">
                                                <Chip
                                                    size="small"
                                                    color="secondary"
                                                    icon={<CalendarIcon fontSize="small" />}
                                                    label={
                                                        <span>
                                                            {dayjs(consultation.startDate).isSame(dayjs(), 'day')
                                                                ? t('moment:calendar:today')
                                                                : dayjs(consultation.startDate).format('DD/MM/YY')
                                                            }{' '}<b>{dayjs(consultation.startDate).format('HH[h]mm')}</b>
                                                        </span>
                                                    }
                                                />
                                                <Chip
                                                    size="small"
                                                    color={statusColor}
                                                    label={t(`common:model.consultation.status.${consultation.status}.label`)}
                                                />
                                            </Stack>
                                            <IconButton
                                                size="small"
                                                onClick={() => window.open(`/consultations/${consultation.id}`, '_blank')}
                                            >
                                                <LaunchIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>

                                        {/* Row 2: people */}
                                        <Stack direction="row" spacing={2} flexWrap="wrap">
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <PatientIcon isFemale={consultation.patient.gender === 'F'} fontSize="small" />
                                                <Typography variant="body2">
                                                    {_.trim(`${consultation.patient.name} ${consultation.patient.surname}`)}
                                                </Typography>
                                            </Stack>
                                            {consultation.doctor && (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <DoctorIcon isFemale={consultation.doctor.gender === 'F'} fontSize="small" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {_.trim(`${consultation.doctor.name} ${consultation.doctor.surname}`)}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {consultation.nurse && (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <PdsIcon isFemale={consultation.nurse.gender === 'F'} fontSize="small" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {_.trim(`${consultation.nurse.name} ${consultation.nurse.surname}`)}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>

                                        {/* Row 3: timing + billing review */}
                                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                            {consultation.waitingTime > 0 && (
                                                <Chip
                                                    size="small"
                                                    color={consultation.waitingTime > 30 ? 'warning' : 'default'}
                                                    label={`${t('page.consultations.table.header.waitingTime', { defaultValue: 'Wait' })}: ${consultation.waitingTime}min`}
                                                />
                                            )}
                                            {consultation.consultationTime > 0 && (
                                                <Chip
                                                    size="small"
                                                    label={`${consultation.consultationTime}min`}
                                                />
                                            )}
                                            <BillingReviewChip review={consultation.billingReview} />
                                        </Stack>

                                        {/* Row 4: desmos + payment */}
                                        {(showDesmosStatus || showPaymentInfo) && (
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                {showDesmosStatus && (
                                                    <Stack direction="row" alignItems="center">
                                                        <Typography variant="caption" color="text.secondary">Desmos:</Typography>
                                                        <DesmosStatusIcon
                                                            status={consultation.desmosActStatus}
                                                            onClick={() => handleDesmosStatusClick(consultation)}
                                                            t={t}
                                                        />
                                                    </Stack>
                                                )}
                                                {showPaymentInfo && (
                                                    <>
                                                        <Chip size="small" label={formatPrice(consultation.price)} />
                                                        <Chip size="small" label={getPaymentTypeLabel(consultation.paymentType)} />
                                                    </>
                                                )}
                                            </Stack>
                                        )}

                                        {/* Row 5: billing details */}
                                        {showBillingDetails && (
                                            <Typography variant="caption" color="text.secondary">
                                                {consultation.patient.secu && `Sécu: ${consultation.patient.secu}`}
                                                {consultation.patient.regime && ` · Rég: ${consultation.patient.regime}`}
                                                {consultation.patient.caisse && ` · Caisse: ${consultation.patient.caisse}`}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </Paper>
                        )
                    })}
                </Stack>
            )}

            {/* Desmos Acts Detail Dialog */}
            {selectedConsultation && (
                <DesmosActsDetailDialog
                    open={desmosDialogOpen}
                    onClose={handleCloseDesmosDialog}
                    consultationId={selectedConsultation.id}
                    desmosActStatus={selectedConsultation.desmosActStatus}
                    networkId={networkId ?? ''}
                    waitingRoomId={waitingRoomId ?? ''}
                />
            )}

            {/* Mass Billing Dialog */}
            <MassBillingDialog
                open={massBillingDialogOpen}
                onClose={handleCloseMassBillingDialog}
                consultationIds={selectedRows}
                consultations={consultations}
            />
        </>
    )
}